import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconLayoutDashboard,
  IconBuildingSkyscraper,
  IconHome2,
  IconUsersGroup,
  IconAffiliate,
  IconFileDescription,
  IconPhoto,
  IconUserCog,
  IconSettings,
  IconBell,
  IconLifebuoy,
  IconLogout,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import { Logo } from "@/components/ui/logo";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { cn } from "@/lib/utils/cn";

type ItemTone = "brand" | "warning" | "success" | "info" | "pink" | "orange";

interface NavItem {
  to: string;
  key: string;
  icon: TablerIcon;
  tone: ItemTone;
}

const items: NavItem[] = [
  { to: "/dashboard", key: "dashboard", icon: IconLayoutDashboard, tone: "brand" },
  { to: "/launches", key: "launches", icon: IconBuildingSkyscraper, tone: "brand" },
  { to: "/units", key: "units", icon: IconHome2, tone: "info" },
  { to: "/brokers", key: "brokers", icon: IconUsersGroup, tone: "success" },
  { to: "/network", key: "network", icon: IconAffiliate, tone: "info" },
  { to: "/proposals", key: "proposals", icon: IconFileDescription, tone: "orange" },
  { to: "/media", key: "media", icon: IconPhoto, tone: "pink" },
  { to: "/notifications", key: "notifications", icon: IconBell, tone: "warning" },
  { to: "/support", key: "support", icon: IconLifebuoy, tone: "pink" },
  { to: "/users", key: "users", icon: IconUserCog, tone: "info" },
  { to: "/settings", key: "settings", icon: IconSettings, tone: "brand" },
];

const toneActiveBg: Record<ItemTone, string> = {
  brand: "bg-gradient-to-r from-brand-500/25 to-brand-500/5",
  info: "bg-gradient-to-r from-info/25 to-info/5",
  success: "bg-gradient-to-r from-success/25 to-success/5",
  warning: "bg-gradient-to-r from-warning/30 to-warning/5",
  pink: "bg-gradient-to-r from-pink/25 to-pink/5",
  orange: "bg-gradient-to-r from-orange/25 to-orange/5",
};

const toneIcon: Record<ItemTone, string> = {
  brand: "bg-brand-500/15 text-brand-300",
  info: "bg-info/15 text-purple-300",
  success: "bg-success/15 text-emerald-300",
  warning: "bg-warning/15 text-amber-300",
  pink: "bg-pink/15 text-pink-300",
  orange: "bg-orange/15 text-orange-300",
};

const toneIconActive: Record<ItemTone, string> = {
  brand: "bg-brand-grad text-white shadow-glow",
  info: "bg-gradient-to-br from-info to-brand-500 text-white shadow-glow-purple",
  success: "bg-argon-grad-success text-white",
  warning: "bg-gradient-to-br from-warning to-orange text-white",
  pink: "bg-argon-grad-warm text-white shadow-glow-pink",
  orange: "bg-gradient-to-br from-orange to-pink text-white",
};

export function Sidebar({ onItemClick }: { onItemClick?: () => void }) {
  const { t } = useTranslation();
  const logout = useAuthStore((s) => s.logout);

  return (
    <aside className="relative flex h-full w-[228px] flex-col overflow-hidden bg-sidebar-grad text-slate-200 shadow-[8px_0_24px_-12px_rgba(8,12,32,0.55)]">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-20 top-0 h-72 w-72 rounded-full bg-brand-500/10 blur-3xl"
      />

      <div className="relative z-10 flex items-center justify-between gap-3 border-b border-white/5 px-5 py-3">
        <Logo invert />
        <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white/60 backdrop-blur">
          B2B
        </span>
      </div>

      <nav className="scrollbar-thin relative z-10 flex-1 overflow-y-auto px-3 py-3">
        <ul className="flex flex-col gap-0.5">
          {items.map((item) => {
              const Icon = item.icon;
              const label = t(`nav.items.${item.key}`);
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={onItemClick}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition-all",
                        isActive
                          ? cn(toneActiveBg[item.tone], "text-white shadow-inner-light")
                          : "text-slate-300 hover:bg-white/[0.04] hover:text-white",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span
                            aria-hidden
                            className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-white"
                          />
                        )}
                        <span
                          className={cn(
                            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all",
                            isActive ? toneIconActive[item.tone] : toneIcon[item.tone],
                          )}
                        >
                          <Icon size={16} />
                        </span>
                        <span className="flex-1 truncate">{label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
        </ul>
      </nav>

      <div className="relative z-10 border-t border-white/5 p-3">
        <button
          type="button"
          onClick={() => {
            onItemClick?.();
            logout();
          }}
          className="group flex w-full items-center gap-3 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-slate-300 transition-all hover:bg-white/[0.04] hover:text-white"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 text-slate-300 transition-all group-hover:bg-danger/20 group-hover:text-red-300">
            <IconLogout size={16} />
          </span>
          <span className="flex-1 truncate text-left">{t("actions.logout")}</span>
        </button>
      </div>
    </aside>
  );
}
